// Service Worker Registration
export function registerSW() {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', async () => {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/'
        });

        console.log('SW registered: ', registration);

        // Manejar actualizaciones del service worker
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // Nueva versión disponible
                console.log('Nueva versión disponible');

                // Opcional: Mostrar notificación al usuario
                showUpdateNotification(newWorker);
              }
            });
          }
        });

        // Manejar mensajes del service worker
        navigator.serviceWorker.addEventListener('message', (event) => {
          if (event.data && event.data.type === 'SW_UPDATED') {
            // El SW se ha actualizado
            window.location.reload();
          }
        });

      } catch (registrationError) {
        console.log('SW registration failed: ', registrationError);
      }
    });
  }
}

// Función para mostrar toast de actualización
function showUpdateToast() {
  // Crear toast de notificación
  const toast = document.createElement('div');
  toast.style.cssText = `
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: #4caf50;
    color: white;
    padding: 12px 20px;
    border-radius: 6px;
    z-index: 10000;
    font-family: "Poppins", sans-serif;
    font-size: 14px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    animation: slideDown 0.3s ease-out;
  `;

  toast.textContent = '✓ Aplicación actualizada correctamente';
  document.body.appendChild(toast);

  // Remover el toast después de 3 segundos
  setTimeout(() => {
    if (document.body.contains(toast)) {
      toast.style.animation = 'slideUp 0.3s ease-out forwards';
      setTimeout(() => {
        if (document.body.contains(toast)) {
          document.body.removeChild(toast);
        }
      }, 300);
    }
  }, 3000);
}

// Función para mostrar notificación de actualización
function showUpdateNotification(worker: ServiceWorker) {
  // Actualización automática sin confirmación del usuario
  console.log('Nueva versión disponible, actualizando automáticamente...');

  // Enviar mensaje al service worker para que se active inmediatamente
  worker.postMessage({ type: 'SKIP_WAITING' });

  // Opcional: Mostrar un toast o notificación breve
  showUpdateToast();
}

// Interfaces para el tipo de dispositivo
interface DeviceInfo {
  isIOS: boolean;
  isSafari: boolean;
  isChrome: boolean;
  isAndroid: boolean;
  isMobile: boolean;
  supportsInstallPrompt: boolean;
}

// Función para verificar si la app está instalada
export function checkIfPWAInstalled() {
  // Verificar si está en standalone mode (instalada)
  if (window.matchMedia('(display-mode: standalone)').matches) {
    console.log('PWA está instalada');
    return true;
  }

  // Verificar si está en fullscreen mode (también considerada instalada)
  if (window.matchMedia('(display-mode: fullscreen)').matches) {
    console.log('PWA está en modo fullscreen');
    return true;
  }

  // Verificar para iOS Safari (standalone mode)
  if ((window.navigator as any).standalone === true) {
    console.log('PWA está instalada en iOS Safari');
    return true;
  }

  return false;
}

// Función para detectar el dispositivo y navegador
export function detectBrowserAndDevice(): DeviceInfo {
  const userAgent = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(userAgent);
  const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
  const isChrome = /Chrome/.test(userAgent);
  const isAndroid = /Android/.test(userAgent);

  return {
    isIOS,
    isSafari,
    isChrome,
    isAndroid,
    isMobile: isIOS || isAndroid,
    supportsInstallPrompt: !isIOS && 'BeforeInstallPromptEvent' in window
  };
}

// Variable global para almacenar el contenedor
let installContainer: HTMLElement | null = null;

// Función para mostrar prompt de instalación
export function showInstallPrompt() {
  const deviceInfo = detectBrowserAndDevice();
  let deferredPrompt: any = null;

  // Para navegadores que soportan beforeinstallprompt (Chrome en Android principalmente)
  if (deviceInfo.supportsInstallPrompt) {
    window.addEventListener('beforeinstallprompt', (e) => {
      // Prevenir que Chrome muestre automáticamente el prompt
      e.preventDefault();
      // Guardar el evento para poder dispararlo después
      deferredPrompt = e;

      // Mostrar botón de instalación personalizado
      showInstallButton(deferredPrompt, deviceInfo);
    });
  } else {
    // Para iOS Safari y otros navegadores que no soportan beforeinstallprompt
    // Mostrar las instrucciones manuales después de un pequeño delay
    setTimeout(() => {
      if (!checkIfPWAInstalled() && !wasInstallPromptDismissed()) {
        showInstallButton(null, deviceInfo);
      }
    }, 2000);
  }

  // Manejar cuando el usuario instala la app
  window.addEventListener('appinstalled', (evt) => {
    console.log('PWA instalada exitosamente');
    // Opcional: Analytics o feedback al usuario
    hideInstallButton();
  });
}

function showInstallButton(deferredPrompt: any, deviceInfo: DeviceInfo) {
  // Si ya existe un botón de instalación, no crear otro
  if (installContainer && document.body.contains(installContainer)) {
    return;
  }

  // Crear contenedor para el botón de instalación
  installContainer = document.createElement('div');
  installContainer.id = 'pwa-install-container';
  installContainer.style.cssText = `
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: #7260c2;
    color: white;
    border: none;
    border-radius: 8px;
    z-index: 1000;
    font-family: "Poppins", sans-serif;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 16px 20px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    max-width: 320px;
    min-width: 280px;
    text-align: center;
    animation: slideDown 0.3s ease-out;
  `;

  // Agregar animación CSS
  const style = document.createElement('style');
  style.textContent = `
    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateX(-50%) translateY(-20px);
      }
      to {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
      }
    }
  `;
  document.head.appendChild(style);

  // Contenido diferente según el dispositivo
  let buttonText = 'Instalar App';
  let instructionText = '';

  if (deviceInfo.isIOS && deviceInfo.isSafari) {
    buttonText = 'Ver instrucciones';
    instructionText = 'Para instalar esta app: \n1. Toca el botón Compartir (↗️) \n2. Selecciona "Añadir a pantalla de inicio"';
  } else if (deviceInfo.isIOS && deviceInfo.isChrome) {
    buttonText = 'Ver instrucciones';
    instructionText = 'Para instalar esta app: \n1. Toca los tres puntos (...) en el menú \n2. Selecciona "Añadir a pantalla de inicio"';
  } else if (deferredPrompt) {
    instructionText = '🚀 Instala Beeverso para una mejor experiencia de lectura';
  } else {
    instructionText = '📱 Agrega Beeverso a tu pantalla de inicio para acceso rápido';
  }

  // Agregar texto de instrucciones si existe
  if (instructionText) {
    const instructionElement = document.createElement('p');
    instructionElement.textContent = instructionText;
    instructionElement.style.cssText = `
      margin: 0;
      font-size: 13px;
      line-height: 1.4;
      color: white;
      white-space: pre-line;
    `;
    installContainer.appendChild(instructionElement);
  }

  // Contenedor para botones
  const buttonContainer = document.createElement('div');
  buttonContainer.style.cssText = `
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    justify-content: center;
  `;

  // Crear botón de instalación/instrucciones
  const installButton = document.createElement('button');
  installButton.textContent = buttonText;
  installButton.style.cssText = `
    background: rgba(255, 255, 255, 0.9);
    color: #7260c2;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-family: "Poppins", sans-serif;
    font-size: 14px;
    font-weight: 600;
    padding: 10px 16px;
    transition: all 0.2s ease;
    flex: 1;
  `;

  // Crear botón de cierre (X)
  const closeButton = document.createElement('button');
  closeButton.textContent = '✕';
  closeButton.setAttribute('aria-label', 'Cerrar');
  closeButton.style.cssText = `
    background: transparent;
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    cursor: pointer;
    font-size: 16px;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    font-family: "Poppins", sans-serif;
  `;

  // Hover effects
  installButton.addEventListener('mouseenter', () => {
    installButton.style.background = 'white';
    installButton.style.transform = 'translateY(-1px)';
  });

  installButton.addEventListener('mouseleave', () => {
    installButton.style.background = 'rgba(255, 255, 255, 0.9)';
    installButton.style.transform = 'translateY(0)';
  });

  closeButton.addEventListener('mouseenter', () => {
    closeButton.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
    closeButton.style.transform = 'scale(1.1)';
  });

  closeButton.addEventListener('mouseleave', () => {
    closeButton.style.backgroundColor = 'transparent';
    closeButton.style.transform = 'scale(1)';
  });

  // Event listener para el botón de instalación
  installButton.addEventListener('click', async () => {
    if (deferredPrompt) {
      // Para navegadores que soportan el prompt automático
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        console.log(`User response to the install prompt: ${outcome}`);

        if (outcome === 'accepted') {
          hideInstallButton();
        }

        deferredPrompt = null;
      } catch (error) {
        console.error('Error showing install prompt:', error);
      }
    } else if (deviceInfo.isIOS) {
      // Para iOS, mostrar modal con instrucciones detalladas
      showIOSInstructions(deviceInfo);
    } else {
      // Para otros casos, ocultar el botón
      hideInstallButton();
    }
  });

  // Event listener para el botón de cierre
  closeButton.addEventListener('click', () => {
    hideInstallButton();
    // Guardar en localStorage que el usuario cerró el prompt
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  });

  // Agregar botones al contenedor
  buttonContainer.appendChild(installButton);
  buttonContainer.appendChild(closeButton);
  installContainer.appendChild(buttonContainer);

  // Solo mostrar si no está ya instalada y no ha sido cerrado recientemente
  if (!checkIfPWAInstalled() && !wasInstallPromptDismissed()) {
    document.body.appendChild(installContainer);
  }
}

// Función para mostrar instrucciones detalladas para iOS
function showIOSInstructions(deviceInfo: DeviceInfo) {
  // Crear modal con instrucciones
  const modal = document.createElement('div');
  modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.7);
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    box-sizing: border-box;
  `;

  const modalContent = document.createElement('div');
  modalContent.style.cssText = `
    background: white;
    border-radius: 12px;
    padding: 24px;
    max-width: 350px;
    width: 100%;
    text-align: center;
    font-family: "Poppins", sans-serif;
  `;

  let instructions = '';
  if (deviceInfo.isSafari) {
    instructions = `
      <h3 style="color: #7260c2; margin: 0 0 16px 0; font-size: 18px;">Instalar Beeverso</h3>
      <p style="margin: 0 0 16px 0; color: #333; line-height: 1.5;">Para instalar esta aplicación en tu dispositivo:</p>
      <ol style="text-align: left; color: #666; line-height: 1.6; padding-left: 20px;">
        <li>Toca el botón <strong>Compartir</strong> (↗️) en la barra inferior</li>
        <li>Desplázate hacia abajo y busca <strong>"Añadir a pantalla de inicio"</strong></li>
        <li>Toca en esa opción</li>
        <li>Confirma tocando <strong>"Añadir"</strong></li>
      </ol>
    `;
  } else {
    instructions = `
      <h3 style="color: #7260c2; margin: 0 0 16px 0; font-size: 18px;">Instalar Beeverso</h3>
      <p style="margin: 0 0 16px 0; color: #333; line-height: 1.5;">Para instalar esta aplicación en tu dispositivo:</p>
      <ol style="text-align: left; color: #666; line-height: 1.6; padding-left: 20px;">
        <li>Toca los <strong>tres puntos (...)</strong> en la esquina superior derecha</li>
        <li>Busca y toca <strong>"Añadir a pantalla de inicio"</strong></li>
        <li>Confirma la instalación</li>
      </ol>
    `;
  }

  modalContent.innerHTML = instructions;

  const closeModalButton = document.createElement('button');
  closeModalButton.textContent = 'Entendido';
  closeModalButton.style.cssText = `
    background: #7260c2;
    color: white;
    border: none;
    border-radius: 6px;
    padding: 12px 24px;
    font-family: "Poppins", sans-serif;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    margin-top: 16px;
    transition: background-color 0.2s;
  `;

  closeModalButton.addEventListener('click', () => {
    document.body.removeChild(modal);
    hideInstallButton();
  });

  closeModalButton.addEventListener('mouseenter', () => {
    closeModalButton.style.backgroundColor = '#5a4ba3';
  });

  closeModalButton.addEventListener('mouseleave', () => {
    closeModalButton.style.backgroundColor = '#7260c2';
  });

  modalContent.appendChild(closeModalButton);
  modal.appendChild(modalContent);
  document.body.appendChild(modal);

  // Cerrar modal al tocar fuera
  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      document.body.removeChild(modal);
      hideInstallButton();
    }
  });
}

// Función para ocultar el botón de instalación
export function hideInstallButton() {
  if (installContainer && document.body.contains(installContainer)) {
    installContainer.style.animation = 'slideUp 0.3s ease-out forwards';
    setTimeout(() => {
      if (installContainer && document.body.contains(installContainer)) {
        document.body.removeChild(installContainer);
      }
    }, 300);
  }

  // Agregar animación de salida
  const existingStyle = document.querySelector('style');
  if (existingStyle) {
    existingStyle.textContent += `
      @keyframes slideUp {
        from {
          opacity: 1;
          transform: translateX(-50%) translateY(0);
        }
        to {
          opacity: 0;
          transform: translateX(-50%) translateY(-20px);
        }
      }
    `;
  }
}

// Función para resetear el estado del prompt de instalación
export function resetInstallPromptState() {
  localStorage.removeItem('pwa-install-dismissed');
  console.log('PWA install prompt state reset');
}

// Función para verificar si el prompt fue cerrado recientemente
export function wasInstallPromptDismissed() {
  const dismissedTime = localStorage.getItem('pwa-install-dismissed');
  if (!dismissedTime) return false;

  // Verificar si han pasado más de 7 días (604800000 ms)
  const sevenDaysAgo = Date.now() - 604800000;
  return parseInt(dismissedTime) > sevenDaysAgo;
}

// Función para forzar recarga del manifest
export function forceManifestReload() {
  // Agregar timestamp para evitar caché del navegador
  const timestamp = Date.now();
  const link = document.querySelector('link[rel="manifest"]') as HTMLLinkElement;

  if (link) {
    const originalHref = link.href.split('?')[0]; // Remover query params existentes
    link.href = `${originalHref}?v=${timestamp}`;
    console.log('Manifest reloaded with timestamp:', timestamp);
  }
}

// Función para limpiar caché del manifest
export function clearManifestCache() {
  if ('caches' in window) {
    caches.keys().then(function(cacheNames) {
      return Promise.all(
        cacheNames.map(function(cacheName) {
          return caches.open(cacheName).then(function(cache) {
            return cache.keys().then(function(requests) {
              return Promise.all(
                requests
                  .filter(request => request.url.includes('/manifest.json'))
                  .map(request => {
                    console.log('Clearing manifest cache from:', cacheName);
                    return cache.delete(request);
                  })
              );
            });
          });
        })
      );
    }).then(() => {
      console.log('Manifest cache cleared successfully');
    });
  }
}

// Función para inicializar PWA
export function initPWA() {
  // Forzar recarga del manifest y limpiar caché
  forceManifestReload();
  clearManifestCache();

  // Registrar service worker
  registerSW();

  // Configurar prompt de instalación después de que la página cargue
  window.addEventListener('load', () => {
    setTimeout(() => {
      showInstallPrompt();
    }, 3000); // Esperar 3 segundos después de cargar
  });

  // Escuchar cambios en el estado de la aplicación
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && !checkIfPWAInstalled()) {
      // La aplicación volvió a ser visible y no está instalada
      setTimeout(() => {
        if (!wasInstallPromptDismissed()) {
          showInstallPrompt();
        }
      }, 1000);
    }
  });
}
